import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE,
  DOC_UPLOAD_ITEM_WIDGET_VALID_STATE,
  DOC_UPLOAD_PAGE_STATE,
  EDM_DOC_TYPE,
  EDM_REVIEW_STATUS,
} from "../../utility/enums/common";
import { RootState } from "../store";
import { DocumentWidget } from "../../utility/types/common";

// TODO: will be replaced with real data
export const TEST_DOC_WIDGETS: DocumentWidget[] = [
  {
    documentId: "asdf",
    draftId: "draftId",
    documentTypeId: EDM_DOC_TYPE.BIRTH_CERTIFICATE,
    fileName: "testFile",
    contentType: "jpg",
    contentSize: "7MB",
    reviewStatus: EDM_REVIEW_STATUS.APPROVED,
    state: DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.FAILED_UPLOAD,
  },
  {
    documentId: "asdf1",
    draftId: "draftId1",
    documentTypeId: EDM_DOC_TYPE.BIRTH_CERTIFICATE,
    fileName: "testFile",
    contentType: "jpg",
    contentSize: "7MB",
    reviewStatus: EDM_REVIEW_STATUS.APPROVED,
    state: DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.NOT_SUPPORTED_FORMAT,
  },
  {
    documentId: "asdf2",
    draftId: "draftId2",
    documentTypeId: EDM_DOC_TYPE.BIRTH_CERTIFICATE,
    fileName: "testFile",
    contentType: "jpg",
    contentSize: "17MB",
    reviewStatus: EDM_REVIEW_STATUS.REJECTED,
    state: DOC_UPLOAD_ITEM_WIDGET_NON_VALID_STATE.SIZE_EXCEEDED,
  },
  {
    documentId: "asdf3",
    draftId: "draftId3",
    documentTypeId: EDM_DOC_TYPE.BIRTH_CERTIFICATE,
    fileName: "testFile",
    contentType: "jpg",
    contentSize: "7MB",
    reviewStatus: EDM_REVIEW_STATUS.APPROVED,
    state: DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.PROCESSING,
  },
  {
    documentId: "asdf4",
    draftId: "draftId4",
    documentTypeId: EDM_DOC_TYPE.BIRTH_CERTIFICATE,
    fileName: "testFile",
    contentType: "jpg",
    contentSize: "7MB",
    reviewStatus: EDM_REVIEW_STATUS.APPROVED,
    state: DOC_UPLOAD_ITEM_WIDGET_VALID_STATE.SUCCESSFUL,
  },
];

export interface UploadFileMeta {
  imageBase64: string;
  name: string;
  type: string;
  side: string;
}

export enum CandidateBgcAttachmentSubType {
  FRONT = "Front",
  BACK = "Back",
}

export enum FileChangeEvent {
  ADDED = "added",
  REMOVED = "removed",
}
export interface DocUploadState {
  currentPage: DOC_UPLOAD_PAGE_STATE;
  docsWidget: DocumentWidget[];
  isSubmitButtonEnabled: boolean;
  requiredPages: Array<"Front" | "Back">;
  files: Record<string, UploadFileMeta | undefined>;
}

export const initialState: DocUploadState = {
  // currentPage: DOC_UPLOAD_PAGE_STATE.SELECT_FILE_TO_UPLOAD,
  currentPage: DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE,
  docsWidget: TEST_DOC_WIDGETS, // TODO: replace the current state with real data
  isSubmitButtonEnabled: false,
  requiredPages: ["Front", "Back"],
  files: {},
};

export const docUploadSlice = createSlice({
  name: "docUpload",
  initialState,
  reducers: {
    setActivePage: (state, action: PayloadAction<{ page: DocUploadState["currentPage"] }>) => {
      state.currentPage = action.payload.page;
    },
    toggleSubmitButton: (state, action: PayloadAction<{ isEnabled: boolean }>) => {
      const { isEnabled } = action.payload;
      state.isSubmitButtonEnabled = isEnabled;
    },
    setFile: (state, action: PayloadAction<UploadFileMeta>) => {
      state.files[action.payload.side] = action.payload;
    },
    removeFile: (state, action: PayloadAction<UploadFileMeta>) => {
      state.files[action.payload.side] = undefined;
    },
    removeFromDocsWidget: (state, action: PayloadAction<{ documentId: string }>) => {
      const { documentId } = action.payload;
      const docsWidget = state.docsWidget.map((widget) => {
        if (widget.documentId === documentId) return { ...widget, markForDeletion: true };

        return widget;
      });

      state.docsWidget = docsWidget;
    },
    updateDocsWidgetFromEDM: (state, action: PayloadAction<{ documents: DocumentWidget[] }>) => {
      const { documents } = action.payload;

      // Not care about any incoming data if docs already markForDeletion
      const retainedDocs: DocumentWidget[] = [];
      const retainedDocIds: string[] = [];

      const markForDeletionDocs = state.docsWidget.map((doc) => {
        if (doc.markForDeletion) return doc.documentId;
      });

      documents.forEach((doc) => {
        if (!markForDeletionDocs.includes(doc.documentId)) {
          retainedDocs.push(doc);
          retainedDocIds.push(doc.documentId);
        }
      });

      const currentDocsWithoutIncomingIds = state.docsWidget.filter((doc) => !retainedDocIds.includes(doc.documentId));

      state.docsWidget = [...currentDocsWithoutIncomingIds, ...retainedDocs];
    },
  },
});

export const getDocWidgets = (state: RootState) => state.docUpload.docsWidget;
export const {
  setActivePage,
  setFile,
  removeFile,
  toggleSubmitButton,
  removeFromDocsWidget,
  updateDocsWidgetFromEDM,
} = docUploadSlice.actions;

export const getCurrentPage = (state: RootState) => state.docUpload.currentPage;
export const getIsSubmitButtonEnabled = (state: RootState) => state.docUpload.isSubmitButtonEnabled;
export const getRequiredPages = (state: RootState) => state.docUpload.requiredPages;
export const getFiles = (state: RootState) => state.docUpload.files;

export default docUploadSlice.reducer;
